import React, { useMemo, useRef } from 'react';
import { Color, Group } from 'three';
import { useFrame } from '@react-three/fiber';

export interface SatelliteProps {
  scale: number;
  baseColor: string; // hex or css
  emissiveColor: string; // hex or css
  variant?: number; // 0..3
  reveal?: number; // 0..1
  seed?: number; // to slightly vary proportions
}

// Low-poly satellites built from basic primitives to keep perf high
// Variants: 0 probe, 1 cubesat, 2 ring-drone, 3 faceted moonlet
const Satellite: React.FC<SatelliteProps> = ({
  scale,
  baseColor,
  emissiveColor,
  variant = 0,
  reveal = 1,
  seed = 0,
}) => {
  const rootRef = useRef<Group>(null);
  const spinRef = useRef<Group>(null);

  // Slight per-instance offsets
  const rnd = useMemo(() => {
    const s = Math.sin(seed * 9301 + 49297) % 1;
    const n = Math.abs(s);
    return {
      body: 0.85 + (n % 0.15),
      panel: 0.8 + ((n * 1.7) % 0.4),
      ring: 0.9 + ((n * 2.1) % 0.3),
      tilt: ((n * 2 - 1) * Math.PI) / 12,
    };
  }, [seed]);

  useFrame((_s, dt) => {
    if (rootRef.current) {
      rootRef.current.rotation.y += 0.35 * dt;
      rootRef.current.rotation.z += 0.08 * dt;
    }
    if (spinRef.current) {
      spinRef.current.rotation.y += 0.6 * dt;
    }
  });

  const col = useMemo(() => new Color(baseColor).offsetHSL(0.02, -0.3, 0.25), [baseColor]);
  const emi = useMemo(() => new Color(emissiveColor).offsetHSL(-0.06, 0.1, 0.2), [emissiveColor]);

  return (
    <group ref={rootRef} scale={scale}>
      {variant === 0 && (
        // Probe: cylinder body + solar panels + dish + antenna
        <group ref={spinRef} rotation={[0, 0, rnd.tilt]}>
          <mesh>
            <cylinderGeometry args={[0.28 * rnd.body, 0.32 * rnd.body, 0.9 * rnd.body, 12]} />
            <meshStandardMaterial color={col} emissive={emi} emissiveIntensity={0.5} roughness={0.85} metalness={0.12} transparent opacity={reveal} />
          </mesh>
          {/* Solar panels */}
          <group position={[0, 0.02, 0]}>
            <mesh position={[-0.65, 0, 0]}> 
              <boxGeometry args={[0.8 * rnd.panel, 0.04, 0.25]} />
              <meshStandardMaterial color={col.clone().offsetHSL(0.05, -0.15, -0.05)} emissive={emi} emissiveIntensity={0.3} roughness={0.7} metalness={0.2} transparent opacity={reveal} />
            </mesh>
            <mesh position={[0.65, 0, 0]}>
              <boxGeometry args={[0.8 * rnd.panel, 0.04, 0.25]} />
              <meshStandardMaterial color={col.clone().offsetHSL(0.05, -0.15, -0.05)} emissive={emi} emissiveIntensity={0.3} roughness={0.7} metalness={0.2} transparent opacity={reveal} />
            </mesh>
          </group>
          {/* Dish */}
          <group position={[0, 0.45, 0]} rotation={[Math.PI / 2.4, 0, 0]}>
            <mesh>
              <coneGeometry args={[0.22, 0.18, 10]} />
              <meshStandardMaterial color={col.clone().offsetHSL(0.0, -0.25, 0.05)} emissive={emi} emissiveIntensity={0.25} roughness={0.6} metalness={0.25} transparent opacity={reveal} />
            </mesh>
            <mesh position={[0, 0.12, 0]}>
              <cylinderGeometry args={[0.02, 0.02, 0.18, 8]} />
              <meshStandardMaterial color={col.clone().offsetHSL(-0.04, -0.25, 0.2)} emissive={emi} emissiveIntensity={0.15} roughness={0.6} metalness={0.35} transparent opacity={reveal} />
            </mesh>
          </group>
        </group>
      )}

      {variant === 1 && (
        // CubeSat: box with panels and a small antenna
        <group ref={spinRef} rotation={[0, 0, rnd.tilt]}>
          <mesh>
            <boxGeometry args={[0.48, 0.48, 0.48]} />
            <meshStandardMaterial color={col} emissive={emi} emissiveIntensity={0.45} roughness={0.9} metalness={0.1} transparent opacity={reveal} />
          </mesh>
          <mesh position={[-0.5, 0, 0]}>
            <boxGeometry args={[0.7 * rnd.panel, 0.06, 0.24]} />
            <meshStandardMaterial color={col.clone().offsetHSL(0.02, -0.2, 0.0)} emissive={emi} emissiveIntensity={0.28} roughness={0.7} metalness={0.22} transparent opacity={reveal} />
          </mesh>
          <mesh position={[0.5, 0, 0]}>
            <boxGeometry args={[0.7 * rnd.panel, 0.06, 0.24]} />
            <meshStandardMaterial color={col.clone().offsetHSL(0.02, -0.2, 0.0)} emissive={emi} emissiveIntensity={0.28} roughness={0.7} metalness={0.22} transparent opacity={reveal} />
          </mesh>
          <group position={[0, 0.42, 0]}>
            <mesh>
              <cylinderGeometry args={[0.02, 0.02, 0.28, 8]} />
              <meshStandardMaterial color={col.clone().offsetHSL(-0.02, -0.2, 0.15)} emissive={emi} emissiveIntensity={0.15} roughness={0.6} metalness={0.35} transparent opacity={reveal} />
            </mesh>
            <mesh position={[0, 0.18, 0]}>
              <sphereGeometry args={[0.06, 10, 10]} />
              <meshStandardMaterial color={col.clone().offsetHSL(0.08, -0.15, 0.25)} emissive={emi} emissiveIntensity={0.25} roughness={0.7} metalness={0.2} transparent opacity={reveal} />
            </mesh>
          </group>
        </group>
      )}

      {variant === 2 && (
        // Ring drone: thin torus with inner core and 3 fins
        <group ref={spinRef} rotation={[rnd.tilt, 0, 0]}>
          <mesh rotation={[Math.PI / 2, 0, 0]}> 
            <torusGeometry args={[0.42 * rnd.ring, 0.04, 10, 22]} />
            <meshStandardMaterial color={col.clone().offsetHSL(0.06, -0.2, 0.08)} emissive={emi} emissiveIntensity={0.35} roughness={0.6} metalness={0.35} transparent opacity={reveal} />
          </mesh>
          <mesh>
            <sphereGeometry args={[0.12, 12, 12]} />
            <meshStandardMaterial color={col.clone().offsetHSL(-0.06, -0.2, 0.15)} emissive={emi} emissiveIntensity={0.45} roughness={0.7} metalness={0.25} transparent opacity={reveal} />
          </mesh>
          {[0, 1, 2].map((i) => (
            <group key={i} rotation={[0, (i / 3) * Math.PI * 2, 0]} position={[0.0, 0.0, 0.0]}>
              <mesh position={[0.0, 0.0, 0.38]} rotation={[Math.PI / 3, 0, 0]}>
                <coneGeometry args={[0.08, 0.16, 8]} />
                <meshStandardMaterial color={col.clone().offsetHSL(0.02, -0.2, 0.25)} emissive={emi} emissiveIntensity={0.2} roughness={0.7} metalness={0.25} transparent opacity={reveal} />
              </mesh>
            </group>
          ))}
        </group>
      )}

      {variant === 3 && (
        // Faceted moonlet: dodecahedron slightly squashed for irregular look
        <group ref={spinRef} rotation={[0, 0, rnd.tilt]}>
          <mesh scale={[1.0, 0.82 + rnd.body * 0.15, 1.0]}> 
            <dodecahedronGeometry args={[0.36, 0]} />
            <meshStandardMaterial color={col.clone().offsetHSL(-0.02, -0.2, -0.05)} emissive={emi} emissiveIntensity={0.35} roughness={0.95} metalness={0.05} transparent opacity={reveal} />
          </mesh>
        </group>
      )}
    </group>
  );
};

export default Satellite;
